import { useState, useEffect } from 'react';
import useLdFlags from './useLdFlags';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { sendLog } from '@profile-ui/shared-components';
import { FLAG_NAMES } from '@profile-ui-mono/utilities/constants';

axiosRetry(axios, { retries: 2 });

const useRetireSmsBanner = () => {
  const [showRetireSmsBanner, setShowRetireSmsBanner] = useState(false);
  const [userData, setUserData] = useState(null);
  const [twoFactorAuthType, setTwoFactorAuthType] = useState('');
  const flags = useLdFlags();

  // newUserInfoContract - turn this on (true) when UH team updates to userInfo endpoing in STG and PRD
  const { enabled = false, lastDate = '', userIds = [], newUserInfoContract } = flags[FLAG_NAMES.RETIRE_SMS_BANNER];

  const setUserInfo = userInfo => {
    // Old API contract
    let treatedData = {
      is_2fa_enabled: userInfo['2FaEnabled'],
      ldap_enabled: userInfo.ldapInfo?.ldapEnabled,
      ldap_domain: userInfo.ldapInfo?.domainName,
      preferred_username: userInfo.userName,
      social_userinfo_list: userInfo.socialUserInfoList,
      sub: userInfo.userId,
    };

    if(userInfo?.twoFactorAuthType) {
      /**
       * if old API contract then twoFactorAuthType is available in meMenuUser data
       */
      setTwoFactorAuthType(userInfo?.twoFactorAuthType);
    }

    // new API contract
    if (newUserInfoContract) {
      treatedData = {
        is_2fa_enabled: userInfo.is_2fa_enabled,
        ldap_enabled: userInfo.ldap_enabled,
        ldap_domain: userInfo.ldap_domain,
        preferred_username: userInfo.preferred_username,
        social_userinfo_list: userInfo.social_userinfo_list,
        sub: userInfo.sub,
      };
    }

    setUserData(treatedData);
  };

  useEffect(() => {
    if (!enabled) return;

    (async function get2faSetting() {
      try {
        const { data } = await axios.get(`${window.$profileEnvVars.$REACT_APP_AUTH_SERVICE_DOMAIN}/security/v1/two-factor-auth/setting`, {
          headers: {
            Authorization: `Bearer ${await window.cfp.providers.auth.getAccessToken()}`,
            'Content-Type': 'application/json',
          }
        });
        setTwoFactorAuthType(data.twoFactorAuthType);
      } catch (error) {
        // ID-2F-006 error code means "Two FA setting does not exist for this user."
        if (error && error.response && error.response.data && error.response.data.type !== 'ID-2F-006') {
          sendLog(error, 'useRetireSmsBanner', 'utilities/hooks');
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (userData && twoFactorAuthType) {
      // userInfo.twoFactorAuthType is 'PingAuthenticator' when user has migrated to new PingOne based Auth App
      const isLegacy2Fa =
        userData['is_2fa_enabled'] &&
        twoFactorAuthType !== 'PingAuthenticator';
      let isLdapUser = false;
      if (userData.ldap_enabled && userData.ldap_domain) {
        const { ldap_domain } = userData;
        isLdapUser = ldap_domain !== null && ldap_domain !== '';
      }
      let isPureSocialUser = false;
      if (userData.social_userinfo_list && userData.social_userinfo_list.length > 0) {
        isPureSocialUser =
          userData.social_userinfo_list[0]?.providerName !== '' &&
          userData?.preferred_username === '';
      }
      const isNonLdapOrSocialUser = !isLdapUser && !isPureSocialUser;
      /* Currently need to roll out the banner to specific users. Later on, * can be used in userIds to roll it out for all users */
      /* Don't show banner to LDAP & Pure Social users */
      setShowRetireSmsBanner(
        isNonLdapOrSocialUser &&
        isLegacy2Fa &&
        enabled &&
        (userIds.includes(userData.sub) || userIds.includes('*'))
      );
    }
  }, [userData, twoFactorAuthType]);

  return { showRetireSmsBanner, userData, lastDate, setUserInfo, twoFactorAuthType };
};

export default useRetireSmsBanner;